import { useMemo, useState } from 'react';

import SorterColumn from 'components/Table/components/SorterColumn/SorterColumn';
import { RESPONSIVE_COLUMN_TYPE } from 'components/Table/constants';

/**
 * @description Hook para manejar el orden de datos en una tabla
 * @param {Array} dataSource - Datos de la tabla
 * @returns {Object} - Objeto con los datos ordenados y la función para manejar el orden
 */
export const useSortableTable = (dataSource = []) => {
  const [sortConfig, setSortConfig] = useState({});

  const sortedData = useMemo(() => {
    const sortableData = [...dataSource];

    if (Object.keys(sortConfig).length === 0) return sortableData;

    sortableData.sort((a, b) => {
      const sortField = Object.keys(sortConfig)[0];
      const sortOrder = sortConfig[sortField]?.order;

      if (!sortOrder) return 0;

      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested objects (like users)
      if (sortField === 'users') {
        aValue = a.users?.assigned || 0;
        bValue = b.users?.assigned || 0;
      }

      // Handle string values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sortableData;
  }, [dataSource, sortConfig]);

  const handleSort = sortFields => {
    setSortConfig(sortFields);
  };

  const createSortableColumn = columnConfig => {
    const { key, title, dataIndex } = columnConfig;

    if (key === 'actions' || columnConfig.columnType === RESPONSIVE_COLUMN_TYPE.ACTIONS) {
      return columnConfig;
    }

    return {
      ...columnConfig,
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{title}</span>
          <SorterColumn
            column={dataIndex || key}
            onChange={handleSort}
            defaultValues={[]}
            upIcon="keyboard_arrow_up"
            downIcon="keyboard_arrow_down"
          />
        </div>
      ),
    };
  };

  return {
    sortedData,
    createSortableColumn,
    sortConfig,
  };
};

export default useSortableTable;
